import * as apiService from '../apiService';
import { showToast } from '../toast';
import type { Match, Award } from '../../types';
import { _ } from 'svelte-i18n';

/**
 * Check if match date is today or later
 * @param matchDate - Date string in format "YYYY-MM-DDTHH:mm:00"
 * @returns true if match date is today or later, false otherwise
 */
export function isMatchDateTodayOrLater(matchDate: string): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const match = new Date(matchDate);
    match.setHours(0, 0, 0, 0);
    return match <= today;
}
  
/**
 * Check if match is finished
 * We do not have a match time or enddate, so we now assume that a match takes max 2,5 hours
 * @param matchDate - Date string in format "YYYY-MM-DDTHH:mm:00"
 * @returns true if match date is today or later, false otherwise
 */
export function isMatchFinished(matchDate: string): boolean {
    const now = new Date();
    const match = new Date(matchDate);
    return match.getTime() + (2.5 * 60 * 60 * 1000) <= now.getTime();
  }
  
/**
 * Converts any datetime-like value to a standardized ISO datetime string
 * @param date - Can be Date object, ISO string, or separate date and time strings
 * @param time - Optional time string in HH:mm format
 * @returns string in format "YYYY-MM-DDTHH:mm:00"
 */
export function getDateTimeString(date: Date | string, time?: string): string {
    try {
        // If we have a separate time parameter, handle date and time separately
        if (time) {
            const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
            return `${dateStr}T${time}:00`;
        }

        // If date is already a string in ISO format, standardize it
        if (typeof date === 'string') {
            if (date.includes('T')) {
                // Already has time component
                const [datePart, timePart] = date.split('T');
                const time = timePart.slice(0, 5); // Get HH:mm part
                return `${datePart}T${time}:00`;
            } else {
                // Date only, add midnight as time
                return `${date}T00:00:00`;
            }
        }

        // If date is a Date object, convert to ISO and standardize
        if (date instanceof Date) {
            const datePart = date.toISOString().split('T')[0];
            const timePart = date.toTimeString().slice(0, 5);
            return `${datePart}T${timePart}:00`;
        }

        throw new Error('Invalid date format');
    } catch (error) {
        console.error('Error formatting datetime:', error);
        // Return current datetime as fallback
        const now = new Date();
        return `${now.toISOString().split('T')[0]}T${now.toTimeString().slice(0, 5)}:00`;
    }
}

/**
 * Format date to dd-mm-yyyy
 * @param dateString - Date string in format "YYYY-MM-DDTHH:mm:00"
 * @returns string in format "dd-mm-yyyy"
 */
  export function formatDate(dateString: string, includeTime: boolean = false): string {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year} ${includeTime ? date.toTimeString().slice(0, 5) : ''}`;
  }
  
  /**
   * Format date to dd-mm-yyyy
   * @param dateString - Date string in format "YYYY-MM-DDTHH:mm:00"
   * @returns string in format "dd-mm-yyyy"
   */
  export function formatDateShort(dateString: string): string {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear().toString().slice(2);
    return `${day}/${month}/${year}`;
  }
/**
 * Format time to HH:mm
 * @param dateString - Date string in format "YYYY-MM-DDTHH:mm:00"
 * @returns string in format "HH:mm"
 */
export function formatTime(dateString: string): string {
    const date = new Date(dateString);
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');
    return `${hour}:${minute}`;
  }


export async function saveReportWithImage(report: Match): Promise<Match> {
  try {
    // Remove binary data from report before sending
    const { match_photo, announcement_photo, ...reportDataForSave } = report;
    
    console.log('reportDataForSave', reportDataForSave);
    reportDataForSave.match_date = getDateTimeString(report.match_date);

    let savedReport: Match | null = null;      
    if (report.id) {
      await apiService.updateMatch(report.id.toString(), reportDataForSave, (result: Match, status: number) => {
        if (status === 200) {
          savedReport = result;
        }
      });
    } else {
      await apiService.saveMatch(reportDataForSave, (result: Match, status: number) => {
        if (status === 200) {
          savedReport = result;
        }
      });
    }

    if (!savedReport) {
      throw new Error('Failed to save report');
    }

    return savedReport;

  } catch (error) {
    console.error('Error saving report:', error);
    showToast('Error saving report: ' + error.message, 'error');
    throw error;
  }
}

export function getAwards() {
  /* Note that we may need to translate the labels and icons in svelte components (using the id) */
  return [
    {id: 'player-of-match', label: 'Player of the Match', icon: '👑'},
    // {id: 'best-defense', label: 'Best Defense', icon: '🛡️'},
    // {id: 'team-spirit', label: 'Team Spirit', icon: '🤝'},
    // {id: 'best-assist', label: 'Best Assist', icon: '👟'},
    // {id: 'breakthrough', label: 'Breakthrough', icon: '⭐'}
  ];
}